import type { Settings } from '../settings.ts';

/** Device safe-area insets in CSS px (env(safe-area-inset-*)). */
export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}
export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

export interface Circle {
  cx: number;
  cy: number;
  r: number;
}

/**
 * Bible §3 play screen: the whole screen is the game. Sky + headframe (HUD sign) above, the field
 * full-width in the middle, a mine-floor strip below where the lantern lives. Scale by width,
 * anchor by safe area. No input zone: everything steers except the lantern hit target itself.
 */
export interface Layout {
  w: number;
  h: number;
  insets: Insets;
  cell: number;
  fieldX: number;
  fieldY: number;
  fieldW: number;
  fieldH: number;
  /** Sky band + headframe: 0..topH. */
  topH: number;
  /** Mine floor strip: floorY..h. */
  floorY: number;
  floorH: number;
  /** Headframe sign carrying the HUD. */
  sign: { x: number; y: number; w: number; h: number };
  bust: Circle;
  plate: { x: number; y: number; w: number; h: number };
  /** Variant A lantern: `hit` is the 56 pt target, `visual` the 40 pt drawn lantern. Null for variant B. */
  lantern: { hit: Circle; visual: Circle } | null;
  dpad: { cx: number; cy: number; size: number } | null;
}

/** Minimum band heights (CSS px) before the field is allowed to shrink below full width. */
export const MIN_TOP = 64;
export const MIN_FLOOR = 100;
/** Share of the spare height that goes above the field (the rest is floor). */
export const TOP_SHARE = 0.32;
export const LANTERN_HIT_R = 28; // 56 pt target
export const LANTERN_VISUAL_R = 20; // 40 pt visual

export function computeLayout(w: number, h: number, cols: number, rows: number, settings: Settings, insets: Insets = NO_INSETS): Layout {
  const minTop = MIN_TOP + insets.top;
  const minFloor = MIN_FLOOR + insets.bottom;
  let cell = Math.floor(w / cols);
  if (cell * rows > h - minTop - minFloor) cell = Math.max(8, Math.floor((h - minTop - minFloor) / rows));
  const fieldW = cell * cols;
  const fieldH = cell * rows;
  const spare = h - fieldH;
  const topH = Math.max(minTop, Math.round(spare * TOP_SHARE));
  const floorH = spare - topH;
  const fieldX = Math.floor((w - fieldW) / 2);
  const fieldY = topH;
  const floorY = fieldY + fieldH;

  // Sign: centred in the top band, below the status-bar inset.
  const usableTop = topH - insets.top;
  const signH = Math.min(58, Math.max(44, usableTop * 0.7));
  const signW = Math.min(w - 16, fieldW + 8);
  const sign = { x: Math.floor((w - signW) / 2), y: insets.top + Math.floor((usableTop - signH) / 2) + 2, w: signW, h: signH };
  const bustR = signH * 0.58;
  const bust = { cx: w / 2, cy: sign.y + signH * 0.42, r: bustR };
  const plate = { w: Math.min(150, signW * 0.42), h: 20, x: 0, y: 0 };
  plate.x = Math.floor(w / 2 - plate.w / 2);
  plate.y = sign.y + signH - 6;

  // Lantern (variant A) in the floor strip, thumb-reachable, clear of the bottom inset.
  let lantern: Layout['lantern'] = null;
  if (settings.fireVariant === 'lantern') {
    const margin = 14;
    const cx = settings.fireSide === 'left' ? insets.left + margin + LANTERN_HIT_R : w - insets.right - margin - LANTERN_HIT_R;
    const cy = Math.min(floorY + floorH * 0.5, h - insets.bottom - margin - LANTERN_HIT_R);
    lantern = { hit: { cx, cy, r: LANTERN_HIT_R }, visual: { cx, cy: cy + 2, r: LANTERN_VISUAL_R } };
  }
  const dpadSize = Math.min(floorH - 12 - insets.bottom, w * 0.34);
  const dpad = settings.dpad && dpadSize >= 60
    ? { cx: settings.fireSide === 'left' ? w - insets.right - dpadSize / 2 - 10 : insets.left + dpadSize / 2 + 10, cy: floorY + (floorH - insets.bottom) / 2, size: dpadSize }
    : null;
  return { w, h, insets, cell, fieldX, fieldY, fieldW, fieldH, topH, floorY, floorH, sign, bust, plate, lantern, dpad };
}

export function inCircle(x: number, y: number, cx: number, cy: number, r: number): boolean {
  const dx = x - cx;
  const dy = y - cy;
  return dx * dx + dy * dy <= r * r;
}
